import { useCallback, useEffect, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import {
  shopModeApi,
  type FloorTodayOverview,
} from "../../api/shopMode";

/**
 * The shift is started by the PIN, inside the verify transaction (owner ruling
 * 2026-08-03). This hook used to do it from the browser: on mount it POSTed
 * /shop-mode/shifts/clock-in whenever the overview said one was available.
 * That made the shift start depend on a second request that could fail —
 * `autoClockInAttempted` then blocked any retry for the life of the mount —
 * and on this component mounting at all. Since payroll bills from
 * `Shift.actualStart`, a dropped request meant unpaid worked hours.
 *
 * The endpoint still exists for the explicit path; nothing here calls it.
 */
export function useShopFloorTodayOverview(liveRefresh: boolean) {
  const [overview, setOverview] = useState<FloorTodayOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<FloorTodayOverview | null> => {
    try {
      const next = await shopModeApi.floorTodayOverview();
      setOverview(next);
      setError(null);
      return next;
    } catch (err: unknown) {
      setError(errorText(err, "shift.couldNotLoad"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!liveRefresh) return;
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(timer);
  }, [liveRefresh, refresh]);

  const endShift = useCallback(async () => {
    await shopModeApi.floorClockOut();
    await refresh();
  }, [refresh]);

  return { overview, loading, error, refresh, endShift };
}
